import React, { useState } from 'react';
import { Key, Puzzle, X, AlertTriangle, Eye, EyeOff, LogIn, UserPlus, Copy, Check, Download, Sparkles, Smartphone, Radio, ExternalLink } from 'lucide-react';
import { generateSecretKey, getPublicKey, nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { BCHLogo } from './Layout';

type Tab = 'extension' | 'amber' | 'bunker' | 'nsec' | 'signup';

// Detect Android
const isAndroid = () => /android/i.test(navigator.userAgent);
// Detect iOS
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
// Detect mobile
const isMobile = () => isAndroid() || isIOS();

export default function LoginDialog() {
  const { showLoginDialog, setShowLoginDialog, loginWithExtension, loginWithNsec, loginWithBunker } = useAppContext();

  const [tab, setTab] = useState<Tab>('extension');
  const [hideAdvancedTabs, setHideAdvancedTabs] = useState(false);

  const [nsecInput, setNsecInput] = useState('');
  const [showNsec, setShowNsec] = useState(false);
  const [bunkerInput, setBunkerInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  // Sign up state
  const [generatedKeys, setGeneratedKeys] = useState<{ nsec: string; npub: string } | null>(null);
  const [backedUp, setBackedUp] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Read flags fresh every time the dialog opens
  React.useEffect(() => {
    if (!showLoginDialog) return;
    const wantsSignup = (window as any).__bchnostr_signup;
    const signupOnly = (window as any).__bchnostr_signup_only;
    delete (window as any).__bchnostr_signup;
    delete (window as any).__bchnostr_signup_only;

    if (signupOnly) {
      setTab('signup');
      setHideAdvancedTabs(true);
    } else if (wantsSignup) {
      setTab('signup');
      setHideAdvancedTabs(false);
    } else {
      setTab(isAndroid() ? 'amber' : isIOS() ? 'bunker' : 'extension');
      setHideAdvancedTabs(false);
    }
    setError(null);
    setProgress(null);
    setGeneratedKeys(null);
    setBackedUp(false);
  }, [showLoginDialog]);

  if (!showLoginDialog) return null;

  const hasExtension = !!(window as any).nostr;

  const handleExtensionLogin = async () => {
    setError(null); setLoading(true);
    try { await loginWithExtension(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Extension login failed.'); }
    finally { setLoading(false); }
  };

  const handleNsecLogin = async () => {
    if (!nsecInput.trim()) { setError('Please enter your nsec key'); return; }
    setError(null); setLoading(true);
    try { await loginWithNsec(nsecInput.trim()); setNsecInput(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Invalid nsec key'); }
    finally { setLoading(false); }
  };

  const handleBunkerLogin = async () => {
    if (!bunkerInput.trim()) { setError('Please paste your bunker:// connection string'); return; }
    setError(null); setLoading(true); setProgress(null);
    try {
      await loginWithBunker(bunkerInput.trim(), (msg) => setProgress(msg));
      setBunkerInput('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bunker connection failed');
    } finally { setLoading(false); setProgress(null); }
  };

  const handleAmberLogin = () => {
    // Use NIP-55 to get pubkey from Amber
    // Amber will copy the pubkey to clipboard or call back
    const callbackUrl = `${window.location.origin}/?amber_pubkey=`;
    const url = `nostrsigner:?compressionType=none&returnType=signature&type=get_public_key&callbackUrl=${encodeURIComponent(callbackUrl)}`;
    window.location.href = url;
    // After Amber redirects back, the app will handle ?amber_pubkey= in the URL
  };

  const handleGenerateKeys = () => {
    const sk = generateSecretKey();
    const pk = getPublicKey(sk);
    setGeneratedKeys({ nsec: nip19.nsecEncode(sk), npub: nip19.npubEncode(pk) });
    setBackedUp(false); setError(null);
  };

  const handleSignUpLogin = async () => {
    if (!generatedKeys) return;
    if (!backedUp) { setError("Please confirm you've backed up your keys"); return; }
    setError(null); setLoading(true);
    try { await loginWithNsec(generatedKeys.nsec); }
    catch (e) { setError(e instanceof Error ? e.message : 'Login failed'); }
    finally { setLoading(false); }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const switchTab = (t: Tab) => { setTab(t); setError(null); setProgress(null); };

  // Skill level badge colours
  const levelBadge = (level: 'beginner' | 'intermediate' | 'advanced') => {
    if (level === 'beginner') return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';
    if (level === 'intermediate') return 'bg-amber-500/15 text-amber-600 dark:text-amber-400';
    return 'bg-purple-500/15 text-purple-600 dark:text-purple-400';
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode; level: 'beginner' | 'intermediate' | 'advanced' }[] = [
    ...(isAndroid() ? [{ id: 'amber' as Tab, label: 'Amber', icon: <Smartphone className="w-3.5 h-3.5" />, level: 'beginner' as const }] : []),
    ...(!isAndroid() ? [{ id: 'extension' as Tab, label: 'Extension', icon: <Puzzle className="w-3.5 h-3.5" />, level: 'intermediate' as const }] : []),
    { id: 'bunker' as Tab, label: isIOS() ? 'Signer App' : 'Bunker', icon: <Radio className="w-3.5 h-3.5" />, level: 'intermediate' as const },
    { id: 'nsec' as Tab, label: 'nsec Key', icon: <Key className="w-3.5 h-3.5" />, level: 'advanced' as const },
    { id: 'signup' as Tab, label: 'New Here?', icon: <UserPlus className="w-3.5 h-3.5" />, level: 'beginner' as const },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLoginDialog(false)}>
      <div className="bg-card border border-border rounded-2xl max-w-sm w-full shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center gap-2.5">
            <BCHLogo size={28} />
            <div>
              <h2 className="font-semibold text-sm">
                {hideAdvancedTabs ? 'Create Your Account' : 'Welcome to BCHNostr'}
              </h2>
              <p className="text-[10px] text-muted-foreground">
                {hideAdvancedTabs ? 'No email or phone needed — just keys' : 'Log in or create an account'}
              </p>
            </div>
          </div>
          <button onClick={() => setShowLoginDialog(false)} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs — hidden in signup-only mode */}
        {!hideAdvancedTabs && (
          <div className="flex border-b border-border overflow-x-auto">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => switchTab(t.id)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 px-1 border-b-2 transition-colors ${
                  tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className="flex items-center gap-1 text-[11px] font-medium whitespace-nowrap">
                  {t.icon} {t.label}
                </div>
                <span className={`text-[9px] font-semibold px-1.5 py-0 rounded-full capitalize ${levelBadge(t.level)}`}>
                  {t.level}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="p-5">

          {/* ══ Amber Tab (Android) ══ */}
          {tab === 'amber' && (
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/10 flex items-center justify-center">
                  <Smartphone className="w-8 h-8 text-amber-500" />
                </div>
                <p className="text-sm font-bold">Sign in with Amber</p>
                <p className="text-xs text-muted-foreground">
                  Amber is a Nostr signing app for Android. Your keys never leave the app — BCHNostr just requests signatures.
                </p>
              </div>

              <div className="rounded-xl bg-amber-500/8 border border-amber-500/20 p-3 space-y-1.5">
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">How it works</p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Tap "Open Amber" below</li>
                  <li>Amber opens and asks you to approve</li>
                  <li>You're redirected back and logged in</li>
                </ol>
              </div>

              <button
                onClick={handleAmberLogin}
                className="w-full py-3 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 flex items-center justify-center gap-2 transition-colors"
              >
                <Smartphone className="w-4 h-4" /> Open Amber
              </button>

              <div className="rounded-xl border border-border p-3 space-y-2">
                <p className="text-xs text-muted-foreground">Don't have Amber?</p>
                <a
                  href="https://github.com/greenart7c3/Amber/releases"
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-2 text-xs text-primary hover:underline"
                >
                  <Download className="w-3.5 h-3.5" /> Download Amber (GitHub)
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.greenart7c3.nostrsigner"
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-2 text-xs text-primary hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Get Amber on Google Play
                </a>
              </div>

              <p className="text-[10px] text-muted-foreground text-center">
                Alternatively, use <button onClick={() => switchTab('bunker')} className="text-primary hover:underline">Bunker login</button> or <button onClick={() => switchTab('nsec')} className="text-primary hover:underline">nsec</button>
              </p>
            </div>
          )}

          {/* ══ Bunker / NIP-46 Tab ══ */}
          {tab === 'bunker' && (
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-purple-500/10 flex items-center justify-center">
                  <Radio className="w-8 h-8 text-purple-500" />
                </div>
                <p className="text-sm font-bold">{isIOS() ? 'Sign in with Signer App' : 'Bunker Login (NIP-46)'}</p>
                <p className="text-xs text-muted-foreground">
                  {isIOS()
                    ? 'Use nsec.app or Aegis on iOS. Your keys stay in the signer — BCHNostr never sees them.'
                    : 'Connect a remote signer like nsec.app or Aegis. Your private key never touches this app.'}
                </p>
              </div>

              {/* App options */}
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                  {isIOS() ? 'Recommended for iOS' : 'Supported signers'}
                </p>
                <a
                  href="https://nsec.app"
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0 text-lg">🔐</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold">nsec.app</p>
                    <p className="text-[10px] text-muted-foreground">Web-based signer — works on any browser, iOS & Android</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                </a>
                <a
                  href="https://github.com/ZharlieW/Aegis/releases"
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0 text-lg">🛡\uFE0F</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold">Aegis</p>
                    <p className="text-[10px] text-muted-foreground">Cross-platform signer app — iOS, Android & desktop</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                </a>
              </div>

              {/* How to get the bunker URL */}
              <div className="rounded-xl bg-muted/50 p-3 space-y-1">
                <p className="text-xs font-semibold text-foreground">How to connect</p>
                <ol className="text-[11px] text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Open nsec.app or Aegis and sign in</li>
                  <li>Find "Connect app" or "Bunker" option</li>
                  <li>Copy the <code className="bg-muted px-1 rounded">bunker://...</code> URL</li>
                  <li>Paste it below and tap Connect</li>
                </ol>
              </div>

              {/* Input */}
              <div className="space-y-2">
                <textarea
                  value={bunkerInput}
                  onChange={e => setBunkerInput(e.target.value)}
                  placeholder="bunker://abc123...?relay=wss://relay.example.com&secret=..."
                  rows={3}
                  className="w-full bg-muted rounded-xl px-3 py-2.5 text-xs font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  spellCheck={false}
                  autoComplete="off"
                />
                {progress && (
                  <div className="flex items-center gap-2 text-xs text-primary">
                    <div className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin shrink-0" />
                    {progress}
                  </div>
                )}
              </div>

              <button
                onClick={handleBunkerLogin}
                disabled={loading || !bunkerInput.trim()}
                className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Connecting...</>
                  : <><Radio className="w-4 h-4" /> Connect Signer</>}
              </button>

              <p className="text-[10px] text-muted-foreground text-center">
                {isAndroid()
                  ? <>Android? Try <button onClick={() => switchTab('amber')} className="text-primary hover:underline">Amber instead</button></>
                  : <>No signer app? Use <button onClick={() => switchTab('nsec')} className="text-primary hover:underline">nsec login</button></>
                }
              </p>
            </div>
          )}

          {/* ══ Extension Tab (desktop) ══ */}
          {tab === 'extension' && (
            <div className="space-y-4">
              <div className="text-center">
                <Puzzle className="w-10 h-10 mx-auto text-primary/40 mb-2" />
                <p className="text-sm font-medium">Browser Extension</p>
                <p className="text-xs text-muted-foreground mt-1">Most secure option. Your keys never leave the extension.</p>
              </div>

              {hasExtension ? (
                <button
                  onClick={handleExtensionLogin}
                  disabled={loading}
                  className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <LogIn className="w-4 h-4" />}
                  Connect Extension
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-3">
                    <p className="text-xs text-muted-foreground">No Nostr extension detected. Install one:</p>
                  </div>
                  <a href="https://github.com/nicka/nos2x" target="_blank" rel="noopener" className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted transition-colors">
                    <div className="w-8 h-8 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0"><Puzzle className="w-4 h-4 text-purple-500" /></div>
                    <div><p className="text-xs font-medium">nos2x</p><p className="text-[10px] text-muted-foreground">Chrome / Brave</p></div>
                  </a>
                  <a href="https://getalby.com" target="_blank" rel="noopener" className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted transition-colors">
                    <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0"><Puzzle className="w-4 h-4 text-amber-500" /></div>
                    <div><p className="text-xs font-medium">Alby</p><p className="text-[10px] text-muted-foreground">Chrome / Firefox / Safari</p></div>
                  </a>
                  <p className="text-[10px] text-muted-foreground text-center">
                    On mobile? Use <button onClick={() => switchTab('bunker')} className="text-primary hover:underline">a signer app</button> or <button onClick={() => switchTab('nsec')} className="text-primary hover:underline">nsec Login</button>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ══ nsec Tab ══ */}
          {tab === 'nsec' && (
            <div className="space-y-4">
              <div className="text-center">
                <Key className="w-10 h-10 mx-auto text-primary/40 mb-2" />
                <p className="text-sm font-medium">Log In with Private Key</p>
                <p className="text-xs text-muted-foreground mt-1">Paste your existing nsec key.</p>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground">
                  Your key is stored in this browser's localStorage. For better security, use a signer app instead.
                </p>
              </div>

              <div className="relative">
                <input
                  type={showNsec ? 'text' : 'password'}
                  value={nsecInput}
                  onChange={e => setNsecInput(e.target.value)}
                  placeholder="nsec1..."
                  className="w-full bg-muted rounded-xl pl-3 pr-10 py-3 text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  onKeyDown={e => { if (e.key === 'Enter') handleNsecLogin(); }}
                  autoComplete="off" spellCheck={false}
                />
                <button onClick={() => setShowNsec(!showNsec)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showNsec ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <button
                onClick={handleNsecLogin}
                disabled={loading || !nsecInput.trim()}
                className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <LogIn className="w-4 h-4" />}
                Log In
              </button>

              <p className="text-[10px] text-muted-foreground text-center">
                Don't have a key? <button onClick={() => switchTab('signup')} className="text-primary hover:underline">Create one</button>
              </p>
            </div>
          )}

          {/* ══ Sign Up Tab ══ */}
          {tab === 'signup' && (
            <div className="space-y-4">
              {!generatedKeys ? (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-emerald-500/20 flex items-center justify-center mb-3">
                      <Sparkles className="w-8 h-8 text-primary" />
                    </div>
                    <p className="text-sm font-medium">Create a New Account</p>
                    <p className="text-xs text-muted-foreground mt-1">No email, no phone — just cryptographic keys.</p>
                  </div>
                  <div className="rounded-xl bg-muted/50 p-3 space-y-2 text-[11px] text-muted-foreground">
                    <p><strong className="text-foreground">How it works:</strong> Your identity is a keypair — a public npub others see, and a private nsec only you have.</p>
                    <p><strong className="text-red-500">Your nsec IS your password. Lose it = lose your account forever.</strong></p>
                  </div>
                  <button onClick={handleGenerateKeys} className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white font-bold text-sm hover:opacity-90 flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4" /> Generate My Keys
                  </button>
                  {hideAdvancedTabs && (
                    <p className="text-[10px] text-muted-foreground text-center">
                      Already have a Nostr account?{' '}
                      <button onClick={() => { setHideAdvancedTabs(false); switchTab(isAndroid() ? 'amber' : isIOS() ? 'bunker' : 'extension'); }} className="text-primary hover:underline font-medium">
                        Log in instead
                      </button>
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-2">
                      <Check className="w-6 h-6 text-emerald-500" />
                    </div>
                    <p className="text-sm font-bold text-emerald-600">Keys Generated!</p>
                    <p className="text-[11px] text-muted-foreground mt-1">Back these up NOW. You will never see them again.</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] font-medium text-muted-foreground">Public Key (npub) — share this</label>
                      <button onClick={() => copyToClipboard(generatedKeys.npub, 'npub')} className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
                        {copiedField === 'npub' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                    <div className="bg-muted rounded-xl p-2.5"><code className="text-[10px] font-mono break-all">{generatedKeys.npub}</code></div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] font-medium text-red-500">Private Key (nsec) — KEEP SECRET</label>
                      <button onClick={() => copyToClipboard(generatedKeys.nsec, 'nsec')} className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
                        {copiedField === 'nsec' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                    <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-2.5"><code className="text-[10px] font-mono break-all">{generatedKeys.nsec}</code></div>
                  </div>

                  <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <div className="text-[11px] text-muted-foreground">
                      <p className="font-bold text-red-500 mb-0.5">SAVE YOUR NSEC NOW</p>
                      <p>Store it in a password manager or write it down. Nobody can recover a lost nsec.</p>
                    </div>
                  </div>

                  <label className="flex items-center gap-2.5 p-3 rounded-xl border border-border hover:bg-muted/50 cursor-pointer transition-colors">
                    <input type="checkbox" checked={backedUp} onChange={e => setBackedUp(e.target.checked)} className="w-4 h-4 rounded border-border text-primary focus:ring-primary shrink-0" />
                    <span className="text-xs">I have saved my nsec somewhere safe</span>
                  </label>

                  <button onClick={handleSignUpLogin} disabled={loading || !backedUp} className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <LogIn className="w-4 h-4" />}
                    Log In to My New Account
                  </button>

                  <button onClick={() => { setGeneratedKeys(null); setBackedUp(false); }} className="w-full text-xs text-muted-foreground hover:text-foreground">
                    Generate different keys
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-3 rounded-xl bg-red-500/10 border border-red-500/20 p-3">
              <p className="text-xs text-red-500">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

